import { SurveyV2 } from '@act/shared/data-transfer-objects';
import { ChipColor } from '@act/storybook';
import { timeFormatter } from '@act/shared/utilities';

/** A phase paired with the responses recorded against it. */
export interface AssignedSurveyHistoryPhaseGroup {
  phase: SurveyV2.AssignedSurveyHistoryPhaseDto | null;
  responses: SurveyV2.AssignedSurveyHistoryResponseDto[];
}

/** A state marker rendered next to a response or disposition row. */
export interface AssignedSurveyHistoryChip {
  label: string;
  color: ChipColor;
}

/**
 * Group responses under their phase, preserving the phase order from the API.
 *
 * Responses whose `phaseId` matches no returned phase are collected into a
 * trailing group with a `null` phase rather than dropped — an orphaned response
 * is exactly the kind of anomaly this timeline exists to surface.
 *
 * @param history - Assigned-survey history payload
 * @returns Phase groups in timeline order, orphaned responses last
 */
export function groupHistoryResponsesByPhase(
  history: SurveyV2.AssignedSurveyHistoryDto
): AssignedSurveyHistoryPhaseGroup[] {
  if (!history) {
    return [];
  }

  const groups = history.phases.map(phase => ({
    phase,
    responses: history.responses.filter(
      response => response.phaseId === phase.id
    ),
  }));

  const knownPhaseIds = new Set(history.phases.map(phase => phase.id));
  const orphanedResponses = history.responses.filter(
    response => !knownPhaseIds.has(response.phaseId)
  );

  if (orphanedResponses.length > 0) {
    groups.push({ phase: null, responses: orphanedResponses });
  }

  return groups;
}

/**
 * State markers for a response: whether the survey path jumped past it and
 * whether it was soft deleted. An answer with no markers is a live response.
 *
 * @param response - History response row
 * @returns Chips to render on the response row, empty when the row is live
 */
export function getResponseStateChips(
  response: SurveyV2.AssignedSurveyHistoryResponseDto
): AssignedSurveyHistoryChip[] {
  const chips: AssignedSurveyHistoryChip[] = [];

  if (response.jumpedAt) {
    chips.push({ label: 'Jumped', color: 'warning' });
  }
  if (response.deletedAt) {
    chips.push({ label: 'Deleted', color: 'error' });
  }
  if (response.skipped) {
    chips.push({ label: 'Skipped', color: 'neutral' });
  }

  return chips;
}

/**
 * Format an epoch timestamp for the timeline, including the time of day.
 *
 * @param epochMs - Epoch milliseconds, or null when the column is unset
 * @returns Human-readable date and time, or an em dash when there is no value
 */
export function formatHistoryTimestamp(epochMs: number | null): string {
  if (!epochMs) {
    return '—';
  }

  return timeFormatter.epochToHumanReadableDate({
    date: epochMs,
    includeTime: true,
  });
}

/**
 * One-line answer summary: free text, or the selected option labels.
 *
 * @param response - History response row
 * @returns Display text for the answer, or a placeholder when nothing was given
 */
export function formatResponseAnswer(
  response: SurveyV2.AssignedSurveyHistoryResponseDto
): string {
  if (response.selectedOptions.length > 0) {
    return response.selectedOptions.map(option => option.text).join(', ');
  }
  if (response.textResponse) {
    return response.textResponse;
  }

  return response.skipped ? 'Skipped' : 'No answer recorded';
}
